import React, { useState, useEffect } from 'react';
import api from '../services/api';

// ═══ Hook responsive ═══
const useResponsive = () => {
    const getState = () => {
        const w = window.innerWidth;
        return { isSmall: w <= 480, isMobile: w <= 900 };
    };
    const [state, setState] = useState(getState);
    useEffect(() => {
        const handleResize = () => setState(getState());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return state;
};

// ═══════════════════════════════════════════════════════════
// AdminHistorique — vue d'ensemble des échanges de la plateforme
// À placer dans src/components/
//
// Affiche les échanges en cours et terminés, avec filtres
// et statistiques (durée moyenne, motifs les plus fréquents).
// ═══════════════════════════════════════════════════════════

const AdminHistorique = () => {
    const { isSmall, isMobile } = useResponsive();
    const [relations, setRelations] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    // Filtres
    const [statut, setStatut] = useState('');       // '', 'accepted', 'ended'
    const [motif, setMotif] = useState('');
    const [depuis, setDepuis] = useState('');
    const [page, setPage] = useState(1);

    useEffect(() => {
        fetchStats();
        fetchRelations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchStats = async () => {
        try {
            const response = await api.get('/admin/relations/stats');
            setStats(response.data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchRelations = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statut) params.append('statut', statut);
            if (motif) params.append('motif', motif);
            if (depuis) params.append('depuis', depuis);

            const response = await api.get(`/admin/relations?${params.toString()}`);
            setRelations(response.data);
            setPage(1);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const resetFiltres = () => {
        setStatut(''); setMotif(''); setDepuis('');
        setTimeout(fetchRelations, 0);
    };

    // ═══ Pagination ═══
    const PAR_PAGE = 10;
    const totalPages = Math.max(1, Math.ceil(relations.length / PAR_PAGE));
    const pageCourante = Math.min(page, totalPages);
    const relationsAffichees = relations.slice(
        (pageCourante - 1) * PAR_PAGE,
        pageCourante * PAR_PAGE
    );

    const formatDate = (d) => {
        if (!d) return '—';
        return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // Durée écoulée : jusqu'à la fin, ou jusqu'à aujourd'hui si en cours
    const duree = (debut, fin) => {
        if (!debut) return '—';
        const jours = Math.round(((fin ? new Date(fin) : new Date()) - new Date(debut)) / 86400000);
        if (jours < 1) return '< 1 j';
        if (jours < 31) return `${jours} j`;
        const mois = Math.round(jours / 30);
        return `${mois} mois`;
    };

    const filtresActifs = [statut, motif, depuis].filter(Boolean).length;

    return (
        <div>
            <h2 style={{ ...styles.title, fontSize: isSmall ? '23px' : isMobile ? '26px' : '30px', textAlign: isMobile ? 'center' : 'left' }}>Historique des échanges</h2>

            {/* ═══ STATISTIQUES ═══ */}
            {stats && (
                <>
                    <div style={{ ...styles.statsGrid, gridTemplateColumns: isSmall ? '1fr 1fr' : 'repeat(auto-fit, minmax(180px, 1fr))', gap: isSmall ? '12px' : '18px' }}>
                        <div style={styles.statCard}>
                            <div style={styles.statNumber}>{stats.en_cours}</div>
                            <div style={styles.statLabel}>Échanges en cours</div>
                        </div>
                        <div style={styles.statCard}>
                            <div style={styles.statNumber}>{stats.terminees}</div>
                            <div style={styles.statLabel}>Échanges terminés</div>
                        </div>
                        <div style={styles.statCard}>
                            <div style={styles.statNumber}>{stats.terminees_ce_mois}</div>
                            <div style={styles.statLabel}>Terminés ce mois</div>
                        </div>
                        <div style={styles.statCard}>
                            <div style={styles.statNumber}>
                                {stats.duree_moyenne !== null ? `${stats.duree_moyenne} j` : '—'}
                            </div>
                            <div style={styles.statLabel}>Durée moyenne</div>
                        </div>
                    </div>

                    {/* Motifs les plus fréquents */}
                    {stats.motifs.length > 0 && (
                        <div style={styles.motifsCard}>
                            <h3 style={styles.sectionTitle}>Motifs de fin les plus fréquents</h3>
                            {stats.motifs.map((m, i) => {
                                const max = stats.motifs[0].total || 1;
                                const pct = Math.round((m.total / max) * 100);
                                return (
                                    <div key={i} style={styles.motifLigne}>
                                        <span style={styles.motifNom}>{m.motif}</span>
                                        <div style={styles.motifBarWrap}>
                                            <div style={{ ...styles.motifBar, width: `${pct}%` }} />
                                        </div>
                                        <span style={styles.motifCount}>{m.total}</span>
                                    </div>
                                );
                            })}
                            {stats.sans_motif > 0 && (
                                <p style={styles.sansMotif}>
                                    {stats.sans_motif} rupture{stats.sans_motif > 1 ? 's' : ''} sans motif renseigné.
                                </p>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* ═══ Filtres, tableau et pagination dans un bloc unique ═══ */}
            <div style={styles.blocTableau}>
                <div style={styles.filtresCard}>
                    <h3 style={styles.sectionTitle}>
                        Filtres {filtresActifs > 0 && <span style={styles.filtreBadge}>{filtresActifs}</span>}
                    </h3>
                    <div style={{ ...styles.filtresRow, flexDirection: isSmall ? 'column' : 'row', alignItems: isSmall ? 'stretch' : 'flex-end' }}>
                        <label style={styles.filtreLabel}>
                            Statut
                            <select value={statut} onChange={e => setStatut(e.target.value)} style={styles.filtreInput}>
                                <option value="">Tous</option>
                                <option value="accepted">En cours</option>
                                <option value="ended">Terminés</option>
                            </select>
                        </label>

                        <label style={styles.filtreLabel}>
                            Motif contient
                            <input
                                value={motif}
                                onChange={e => setMotif(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') fetchRelations(); }}
                                placeholder="Ex : religieuse"
                                style={styles.filtreInput}
                            />
                        </label>

                        <label style={styles.filtreLabel}>
                            Depuis le
                            <input
                                type="date"
                                value={depuis}
                                onChange={e => setDepuis(e.target.value)}
                                style={styles.filtreInput}
                            />
                        </label>

                        <button onClick={fetchRelations} style={styles.filtreBtn}>Appliquer</button>
                        {filtresActifs > 0 && (
                            <button onClick={resetFiltres} style={styles.resetBtn}>Réinitialiser</button>
                        )}
                    </div>
                </div>

                {/* ═══ TABLEAU ═══ */}
                {loading ? (
                    <div style={styles.loading}>Chargement...</div>
                ) : relations.length === 0 ? (
                    <div style={styles.empty}>Aucun échange ne correspond à ces critères.</div>
                ) : (
                    <div style={styles.tableWrap}>
                        <table style={styles.table}>
                            <thead>
                                <tr style={styles.tableHeader}>
                                    <th style={styles.th}>Membres</th>
                                    <th style={styles.th}>Statut</th>
                                    <th style={styles.th}>Début</th>
                                    <th style={styles.th}>Fin</th>
                                    <th style={styles.th}>Durée</th>
                                    <th style={styles.th}>Motif</th>
                                    <th style={styles.th}>Terminé par</th>
                                </tr>
                            </thead>
                            <tbody>
                                {relationsAffichees.map(r => (
                                    <tr key={r.id} style={styles.tableRow}>
                                        <td style={styles.td}>
                                            <strong>{r.sender_prenom || r.sender_email}</strong>
                                            <span style={styles.fleche}> ↔ </span>
                                            <strong>{r.receiver_prenom || r.receiver_email}</strong>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={r.status === 'accepted' ? styles.badgeEnCours : styles.badgeTerminee}>
                                                {r.status === 'accepted' ? 'En cours' : 'Terminé'}
                                            </span>
                                        </td>
                                        <td style={styles.td}>{formatDate(r.date_debut)}</td>
                                        <td style={styles.td}>{r.status === 'ended' ? formatDate(r.date_fin) : '—'}</td>
                                        <td style={styles.td}>{duree(r.date_debut, r.date_fin)}</td>
                                        <td style={{ ...styles.td, maxWidth: '260px' }}>
                                            {r.status === 'ended'
                                                ? (r.motif_fin || <span style={styles.vide}>Non renseigné</span>)
                                                : '—'}
                                        </td>
                                        <td style={styles.td}>
                                            {r.status === 'ended'
                                                ? (r.termine_par_prenom || <span style={styles.vide}>—</span>)
                                                : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={styles.pagination}>
                            <span style={styles.paginationInfo}>
                                {relations.length} échange{relations.length > 1 ? 's' : ''}
                                {' · '}page {pageCourante} sur {totalPages}
                                {relations.length === 300 && ' — limite atteinte, affinez les filtres'}
                            </span>
                            {totalPages > 1 && (
                                <div style={styles.paginationBtns}>
                                    <button
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={pageCourante === 1}
                                        style={pageCourante === 1 ? styles.pageBtnDisabled : styles.pageBtn}
                                    >
                                        ‹ Précédent
                                    </button>
                                    <button
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={pageCourante === totalPages}
                                        style={pageCourante === totalPages ? styles.pageBtnDisabled : styles.pageBtn}
                                    >
                                        Suivant ›
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const styles = {
    title: { color: '#3D0F1E', marginBottom: '24px', fontSize: '30px', fontWeight: '700', fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.3px', wordBreak: 'break-word' },
    sectionTitle: { color: '#3D0F1E', fontSize: '20px', margin: '0 0 16px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Cormorant Garamond', Georgia, serif" },

    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '18px', marginBottom: '20px' },
    statCard: { backgroundColor: 'white', minWidth: 0, padding: '24px 18px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', border: '1px solid #EADCE2', minHeight: '132px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '5px', boxSizing: 'border-box' },
    statNumber: { fontSize: '34px', fontWeight: '700', color: '#3D0F1E', fontFamily: "'Cormorant Garamond', Georgia, serif", lineHeight: 1.1, whiteSpace: 'nowrap' },
    statLabel: { fontSize: '13.5px', color: '#8A8A8A', lineHeight: 1.35 },

    motifsCard: { backgroundColor: 'white', padding: '22px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', border: '1px solid #EADCE2', marginBottom: '20px' },
    motifLigne: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' },
    motifNom: { flex: '0 0 min(240px, 45%)', color: '#555', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    motifBarWrap: { flex: 1, height: '8px', backgroundColor: '#F0E4E9', borderRadius: '4px', overflow: 'hidden' },
    motifBar: { height: '100%', background: 'linear-gradient(90deg, #C4688A, #6B1E35)', borderRadius: '4px' },
    motifCount: { flex: '0 0 34px', textAlign: 'right', color: '#3D0F1E', fontSize: '13px', fontWeight: '700' },
    sansMotif: { color: '#999', fontSize: '12.5px', fontStyle: 'italic', margin: '14px 0 0' },

    blocTableau: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', border: '1px solid #EADCE2', overflow: 'hidden', marginBottom: '20px' },
    filtresCard: { minWidth: 0, padding: '20px 22px', borderBottom: '1px solid #F0E4E9' },
    filtresRow: { display: 'flex', alignItems: 'flex-end', gap: '14px', flexWrap: 'wrap' },
    filtreLabel: { display: 'flex', flexDirection: 'column', gap: '6px', color: '#6B1E35', fontSize: '12.5px', fontWeight: '700' },
    filtreInput: { padding: '9px 11px', border: '1.5px solid #EADCE2', borderRadius: '8px', fontSize: '13.5px', color: '#444', outline: 'none', minWidth: 0, width: '100%', boxSizing: 'border-box' },
    filtreBtn: { padding: '10px 22px', background: 'linear-gradient(135deg, #3D0F1E, #6B1E35)', color: '#FBF3EC', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '13.5px', cursor: 'pointer' },
    resetBtn: { padding: '10px 18px', backgroundColor: '#fff', color: '#6B1E35', border: '1.5px solid #EADCE2', borderRadius: '8px', fontWeight: '700', fontSize: '13.5px', cursor: 'pointer' },
    filtreBadge: { backgroundColor: '#C4688A', color: '#3D0F1E', borderRadius: '50%', width: '20px', height: '20px', fontSize: '11px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800' },

    tableWrap: { backgroundColor: 'white', overflowX: 'auto', WebkitOverflowScrolling: 'touch', padding: '6px 20px 0', boxSizing: 'border-box' },
    table: { width: '100%', minWidth: '760px', borderCollapse: 'separate', borderSpacing: 0, borderRadius: '10px', overflow: 'hidden' },
    tableHeader: { backgroundColor: '#3D0F1E' },
    th: { padding: '13px 14px', color: '#C4688A', textAlign: 'left', fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap' },
    tableRow: {},
    td: { padding: '12px 14px', color: '#444', fontSize: '13px', verticalAlign: 'top', borderBottom: '1px solid #F0E4E9' },
    fleche: { color: '#C4688A', fontWeight: '700' },
    vide: { color: '#bbb', fontStyle: 'italic' },
    badgeEnCours: { backgroundColor: 'rgba(44,95,46,0.12)', color: '#5A7A4E', padding: '4px 11px', borderRadius: '20px', fontSize: '11.5px', fontWeight: '700', whiteSpace: 'nowrap' },
    badgeTerminee: { backgroundColor: 'rgba(154,125,136,0.15)', color: '#7a6068', padding: '4px 11px', borderRadius: '20px', fontSize: '11.5px', fontWeight: '700', whiteSpace: 'nowrap' },
    pagination: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap', padding: '14px 20px', borderTop: '1px solid #F0E4E9' },
    paginationInfo: { color: '#8A8A8A', fontSize: '13px' },
    paginationBtns: { display: 'flex', gap: '8px' },
    pageBtn: { padding: '8px 16px', backgroundColor: '#fff', color: '#6B1E35', border: '1.5px solid #EADCE2', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },
    pageBtnDisabled: { padding: '8px 16px', backgroundColor: '#F5F5F7', color: '#BBB', border: '1.5px solid #EEE', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'not-allowed', whiteSpace: 'nowrap' },
    compteur: { padding: '12px 16px', margin: 0, color: '#999', fontSize: '12.5px', borderTop: '1px solid #F0E4E9' },

    loading: { textAlign: 'center', color: '#3D0F1E', padding: '40px', fontWeight: '600' },
    empty: { textAlign: 'center', color: '#888', padding: '40px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #EADCE2' },
};

export default AdminHistorique;